// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.
import clsx from 'clsx'
import Linked from 'shared-components/service/Linked'
import { publicConfig } from 'config'
import {
  gearCandidates,
  gearCandidatesAward,
  gearEditAction,
  gearOwnerMeta,
  gearStatus,
  updateHint,
  type GearListRow,
} from 'lib/community/hub'
import { newReviewHref } from 'lib/community/model'
import type { CommunityPoints, CommunityProduct, CommunityStanding } from 'lib/community/types'
import type { OwnerShelfItem } from 'lib/dealhunt/types'
import { useOwnerTrack } from 'lib/owner-notes/analytics'
import { noteAnchorId, ownNoteLines } from 'lib/owner-notes/band-copy'
import { useDeclaredProduct } from 'lib/owner-notes/declared'
import { useOwnNoteActions } from 'components/owner-notes/useOwnNoteActions'
import FeedInterruption from './FeedInterruption'
import FeedRow from './FeedRow'
import { shelfSlug } from 'lib/community/writer-handoff'
import styles from './MemberHub.module.css'

/**
 * The member's own layer on the Gear tab (Member Hub 1b, phase 3): their rows with status, the
 * "Own one of these?" tiles. Every part renders only from the member's own `/me`
 * (`useOwnMemberMe` in `MemberGear`), after hydration — never in the shared-cached HTML, and
 * gone while "View as visitor" is on. Pure rules in `lib/community/hub.ts`.
 */

/** A shelf row's SoundGuys slug, or null when DealHunt did not send its brand fields — the
 *  leaf alone 404s (#113), and a wrong slug would open the form in create mode. */
const EDIT_ACTION_LABEL: Record<NonNullable<ReturnType<typeof gearEditAction>>, string> = {
  edit: 'Edit review',
  resubmit: 'Rewrite and resubmit',
  resend: 'Rewrite and resubmit · a moderator will check it',
}

function ownSlug(own: OwnerShelfItem): string | null {
  return own.brand_name === undefined && own.brand_slug === undefined ? null : shelfSlug(own)
}

/**
 * One of the member's own reviews: the public row (`FeedRow`) with the owner extension — the
 * private meta line, DealHunt's sentences under the text (`ownNoteLines`: a rejection's
 * `reader_message` in full, held, hidden, an edit being checked), and the right column: the
 * status chip, "Edit review" / "Rewrite and resubmit" (the community form, which opens in edit
 * mode on a product the member has a note on), "Ask for a human look" on a rejected review (the
 * existing appeal, `useOwnNoteActions`), and the six-month hint.
 *
 * `clock` is the browser's time, set after mount: the hint compares DealHunt's
 * `update_opens_at` with it, never with the page's server timestamp.
 */
export const GearOwnerRow: React.VFC<{
  row: GearListRow & { own: OwnerShelfItem }
  now: number
  clock: number | null
  points: CommunityPoints | null
  highlighted: boolean
  /** A write changed the note (the appeal): read `/me` again. */
  onChanged: () => void
}> = ({ row, now, clock, points, highlighted, onChanged }) => {
  const trailingSlash = publicConfig('useTrailingSlash')
  // Community pages have no catalog page type, so `trackOwner` sends nothing here (as on the
  // community form, `ReviewComposer`); the appeal keeps its call for when they get one.
  const trackOwner = useOwnerTrack(null)
  const { own } = row
  const { busy, error, appealed, locked, appeal } = useOwnNoteActions(own.note.id, {
    trackOwner,
    onAppealed: onChanged,
  })
  const status = gearStatus(own.note.status)
  const action = locked ? null : gearEditAction(own.note)
  const slug = row.item.product.slug || ownSlug(own)
  const editHref = action && slug ? newReviewHref(slug, trailingSlash) : null
  const hint = clock === null ? null : updateHint(own.update_opens_at, clock, points)
  const canAppeal = own.note.status === 'rejected' && !appealed && !locked
  const lines = ownNoteLines(own.note)

  return (
    <FeedRow
      item={row.item}
      now={now}
      showAuthor={false}
      anchorId={noteAnchorId(own.note.id)}
      highlighted={highlighted}
      owner={{
        meta: gearOwnerMeta(row, clock ?? now),
        lines: (
          <>
            {lines.map(line => (
              <p
                key={line.text}
                className={line.tone === 'alert' ? styles.ownAlert : styles.ownFine}
              >
                {line.text}
              </p>
            ))}
            {error && (
              <p className={styles.ownAlert} role="alert">
                {error}
              </p>
            )}
          </>
        ),
        aside: (
          <>
            <span className={clsx(styles.ownStatus, styles[`ownStatus_${status.tone}`])}>
              {status.label}
            </span>
            {editHref && (
              <Linked href={editHref} className={styles.ownAction}>
                {action ? EDIT_ACTION_LABEL[action] : null}
              </Linked>
            )}
            {canAppeal && (
              <button
                type="button"
                className={styles.ownAction}
                disabled={busy}
                onClick={() => void appeal()}
              >
                {'Ask for a human look'}
              </button>
            )}
            {appealed && <span className={styles.ownHint}>{'Sent to a moderator.'}</span>}
            {hint && <span className={styles.ownHint}>{hint}</span>}
          </>
        ),
      }}
    />
  )
}

/**
 * "Own one of these? No owner review yet." (design 1b): the product this browser declared it
 * owns (`sg_owner_declared`), else DealHunt's waiting products — minus everything on the
 * member's shelf (`gearCandidates`). "I own it" opens the community form for that product. The
 * points chip follows `gearCandidatesAward`. Nothing when no product is left.
 */
export const GearCandidatesBlock: React.VFC<{
  shelf: OwnerShelfItem[]
  waiting: CommunityProduct[]
  points: CommunityPoints | null
  standing: CommunityStanding | null
}> = ({ shelf, waiting, points, standing }) => {
  const { declared } = useDeclaredProduct()
  const candidates = gearCandidates(declared, waiting, shelf)
  if (!candidates) {
    return null
  }
  return (
    <FeedInterruption
      products={candidates.products}
      points={points}
      title="Own one of these? No owner review yet."
      award={gearCandidatesAward(candidates.kind, points, standing)}
    />
  )
}
